import { useCallback, useMemo } from 'react';

import Box from '@zenbusiness/zui/Box';
import LinearProgress from '@zenbusiness/zui/LinearProgress';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import IconWithHover from '#client/components/IconWithHover';
import NameIcon from '#client/components/NameIcon';
import useBreakpoints from '#client/hooks/useBreakpoints';
import { capitalizeFirstLetter, getLast4digits } from '#client/utils/text';

import { LabelStyled, MenuStyled } from './AccountRow.styles';

export default function AccountRow({
  id,
  name,
  accountType,
  accountNumber,
  button,
  menuItems,
  loading,
  handleOpen,
  handleClose,
  clickOnItemHandler = () => {},
  isOpenModal,
  anchorEl,
  label = null,
  disabled,
  usedAt = null,
  isArchived,
  isACHSupported,
  isWireSupported,
  bankName
}) {
  const theme = useTheme();
  const {
    sizes: { isMobile }
  } = useBreakpoints();

  const accountTypeCapitalize = accountType ? `${capitalizeFirstLetter(accountType)}` : 'Account';
  const accountNumberLast4digits = getLast4digits(accountNumber);

  const open = Boolean(anchorEl);
  const CONTROLLED_BY = `more-button-${id}`;
  const CONTROLLER = `more-menu-${id}}`;

  const isDebitCardAccountType =
    accountType && (accountType === 'debit' || accountType === 'credit');
  const usedAtDashboard = usedAt && usedAt === 'dashboard';
  const accountTypeAndNumberOrBankName = usedAtDashboard ? (
    <Text
      color="primary"
      size={isDebitCardAccountType ? 'md' : 'xs'}
      weight={isDebitCardAccountType ? 'medium' : 'regular'}
    >
      {bankName ||
        `${capitalizeFirstLetter(accountType || '')} ${isDebitCardAccountType ? accountNumberLast4digits : ''}`.trim()}
    </Text>
  ) : (
    <Text
      color="primary"
      size="xs"
    >
      {bankName || `${accountTypeCapitalize}`}
    </Text>
  );

  const rowContent =
    // For debit card accounts, we want to show the account type & number first
    isDebitCardAccountType ? (
      <>
        {accountTypeAndNumberOrBankName}
        <Text
          color="primary"
          size="xs"
        >
          {name}
        </Text>
      </>
    ) : (
      <>
        <Text
          color="primary"
          size="md"
          weight="medium"
        >
          {`${name} ${accountNumberLast4digits}`}
        </Text>
        {accountTypeAndNumberOrBankName}
      </>
    );

  const labelTransferType = useCallback(
    (contentLabel) => (
      <LabelStyled
        aria-label="label"
        sx={{
          backgroundColor: theme.palette.background.grey.dark
        }}
      >
        <Text
          color="primary"
          size={isMobile ? 'xs' : 'sm'}
          textAlign="center"
          whiteSpace="nowrap"
        >
          {contentLabel}
        </Text>
      </LabelStyled>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobile]
  );

  const renderLabelTransferType = useMemo(() => {
    switch (true) {
      case isWireSupported && isACHSupported:
        return (
          <Stack
            direction="row"
            spacing={{ xs: '4px', md: '8px' }}
          >
            {labelTransferType('ACH')}
            {labelTransferType('Wire')}
          </Stack>
        );
      case isACHSupported:
        return labelTransferType('ACH');
      case isWireSupported:
        return labelTransferType('Wire');
      default:
        return null;
    }
  }, [isACHSupported, isWireSupported, labelTransferType]);

  return (
    <>
      <Stack
        aria-label="account row item"
        direction="column"
        onClick={clickOnItemHandler}
        spacing={{ xs: '8px', md: '16px' }}
        sx={{ cursor: usedAtDashboard ? 'pointer' : 'unset' }}
        width="100%"
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Stack
            alignItems={{ xs: 'flex-start', md: 'center' }}
            direction="row"
            spacing="16px"
            sx={{ width: usedAtDashboard ? '100%' : 'auto' }}
          >
            {(!isMobile || usedAtDashboard) && (
              <Box>
                <NameIcon
                  circle
                  name={name}
                  numberOfCharacters={2}
                />
              </Box>
            )}
            <Stack
              alignItems={{ xs: 'flex-start', md: 'center' }}
              flexDirection={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              spacing={{ xs: '4px', md: '24px' }}
              style={{ marginRight: '16px' }}
              width="100%"
            >
              <Stack
                alignItems="start"
                direction="column"
                gap="4px"
                style={{ wordBreak: 'break-word' }}
              >
                {isArchived && !usedAtDashboard && (
                  <Text
                    color="red"
                    size="xs"
                  >
                    Archived
                  </Text>
                )}
                {rowContent}
                {isMobile && renderLabelTransferType}
              </Stack>
              <Stack>{label}</Stack>
            </Stack>
          </Stack>
          <Stack
            alignItems="center"
            direction="row"
            spacing="24px"
          >
            {!isMobile && (
              <Stack
                alignItems="center"
                direction="row"
                spacing="8px"
              >
                {renderLabelTransferType}
                {button}
              </Stack>
            )}
            {usedAtDashboard ? (
              <IconWithHover
                aria-controls={open ? CONTROLLER : undefined}
                aria-expanded={open}
                aria-haspopup
                disabled={disabled}
                disableRipple
                id={CONTROLLED_BY}
                label="menu icon"
                onClick={handleOpen}
                size="sm"
              >
                <SharedAsset
                  alt=""
                  src="/icon/micro/menu-kebab-vertical.svg"
                />
              </IconWithHover>
            ) : (
              <IconWithHover
                aria-controls={open ? CONTROLLER : undefined}
                aria-expanded={open}
                aria-haspopup
                disabled={disabled}
                disableRipple
                id={CONTROLLED_BY}
                label="menu icon"
                onClick={handleOpen}
              >
                <SharedAsset
                  alt=""
                  src="/icon/micro/menu-kebab-vertical.svg"
                />
              </IconWithHover>
            )}
          </Stack>
        </Stack>
        {isMobile && button}
      </Stack>
      {isOpenModal && (
        <MenuStyled
          anchorEl={anchorEl}
          disableAutoFocus
          disableEnforceFocus
          id={CONTROLLER}
          MenuListProps={{
            'aria-labelledby': CONTROLLED_BY
          }}
          onClose={handleClose}
          open={open}
        >
          <Stack
            aria-label="menu list actions"
            justifyContent="center"
            minHeight="40px"
            minWidth="150px"
            spacing="8px"
          >
            {loading ? (
              <LinearProgress
                aria-label="progresbar"
                sx={{ height: '4px' }}
              />
            ) : (
              menuItems
            )}
          </Stack>
        </MenuStyled>
      )}
    </>
  );
}

AccountRow.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  accountType: PropTypes.string,
  accountNumber: PropTypes.string.isRequired,
  button: PropTypes.node,
  menuItems: PropTypes.node.isRequired,
  loading: PropTypes.bool,
  handleOpen: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  clickOnItemHandler: PropTypes.func,
  isOpenModal: PropTypes.bool.isRequired,
  anchorEl: PropTypes.oneOfType([PropTypes.object]),
  label: PropTypes.node,
  disabled: PropTypes.bool,
  usedAt: PropTypes.string,
  isArchived: PropTypes.bool,
  isWireSupported: PropTypes.bool,
  isACHSupported: PropTypes.bool,
  bankName: PropTypes.string
};
